/**
 * JavaScript Tracking Tool
 * Tracks and analyzes JavaScript loaded on a web page
 * Uses Performance API to gather data, and Page Import Map to identify 1st party entrypoints
 */

/**
 * @typedef {Object} ScriptInfo
 * @property {string} name - Script filename
 * @property {string} url - Full URL of the script
 * @property {number} size - Script size in bytes
 * @property {number} transferSize - Transfer size in bytes
 * @property {number} duration - Load duration in milliseconds
 * @property {number} startTime - Start time in milliseconds
 * @property {'script'|'module'} type - Script type
 * @property {boolean} [sizeUnavailable] - True if size info is blocked by CORS
 */

/**
 * @typedef {Object} EntrypointInfo
 * @property {string} name - Entrypoint name
 * @property {string} url - Entrypoint URL
 * @property {ScriptInfo[]} internalModules - Scripts loaded by this entrypoint
 * @property {number} totalSize - Total size of all modules
 */

/**
 * @typedef {Object} ScriptGroup
 * @property {string} name - Display name (could be comma-separated entrypoint names or basePath)
 * @property {string[]} entrypoints - List of entrypoint names in this group
 * @property {string} basePath - Base URL path
 * @property {ScriptInfo[]} scripts - Scripts in this group
 * @property {number} totalSize - Total size of all scripts
 * @property {number} count - Number of scripts
 */

/**
 * @typedef {Object} TrackingData
 * @property {ScriptInfo[]} firstParty - First-party scripts
 * @property {ScriptInfo[]} thirdParty - Third-party scripts
 * @property {EntrypointInfo[]} entrypoints - Entrypoint information
 * @property {Object.<string, ScriptGroup>} groups - Script groups by domain/path
 */

/**
 * @typedef {Object} TrackingSummary
 * @property {Array.<{name: string, count: number, size: number}>} firstParty - First-party entrypoint summary
 * @property {Array.<{name: string, count: number, size: number|undefined}>} thirdParty - Third-party domain summary
 */

// Internal resource buffer — isolates our view from external
// performance.clearResourceTimings() calls and buffer overflows.
/** @type {PerformanceResourceTiming[]} */
const resourceBuffer = [];
const seenResourceKeys = new Set();
let observerInitialized = false;

function resourceKey(entry) {
    // Dedup by URL only. Collapses preload/prefetch pairs and seed/observer
    // overlap into one entry — more accurate for "bytes delivered to this
    // page" than counting each PerformanceResourceTiming separately, which
    // would double-count when a preload and the actual load both appear.
    return entry.name;
}

function isScriptResource(entry) {
    return (
        entry.initiatorType === 'script' ||
        entry.initiatorType === 'module' ||
        entry.name.endsWith('.js') ||
        entry.name.endsWith('.mjs')
    );
}

function addResourceEntry(entry) {
    if (!isScriptResource(entry)) return;
    const key = resourceKey(entry);
    if (seenResourceKeys.has(key)) return;
    seenResourceKeys.add(key);
    resourceBuffer.push(entry);
}

/**
 * Initialize resource tracking: seed from existing entries and subscribe to
 * future ones via PerformanceObserver. Safe to call multiple times.
 *
 * Call this as early as possible in the page lifecycle so we capture entries
 * before any other code can clear the performance buffer.
 */
function init() {
    if (observerInitialized) return;
    observerInitialized = true;

    // buffered:true replays all entries currently in the performance buffer,
    // so no separate seed step is needed.
    try {
        new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                addResourceEntry(entry);
            }
        }).observe({ type: 'resource', buffered: true });
    } catch {
        // Observer didn't attach — buffer stays empty; tracking-lib init still completes.
    }
}

/**
 * Extract filename from a URL
 * @param {string} url - The URL to extract the filename from
 * @returns {string} The filename portion of the URL, or the full URL if parsing fails
 * @example
 * getFileName('https://example.com/path/to/script.js') // Returns 'script.js'
 */
function getFileName(url) {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        return pathname.substring(pathname.lastIndexOf('/') + 1) || pathname;
    } catch {
        return url;
    }
}
/**
 * Get all loaded scripts using the Performance Resource Timing API
 * Collects information about JavaScript resources including size, duration, and type.
 * Detects when size information is unavailable due to CORS restrictions.
 *
 * @returns {ScriptInfo[]} Array of script information objects
 * @remarks
 * - Uses decodedBodySize if available, otherwise falls back to encodedBodySize
 * - For cross-origin resources without Timing-Allow-Origin header, size fields are 0
 * - Filters for resources with initiatorType of 'script' or 'module', or ending in .js/.mjs
 */
function getLoadedScripts() {
    const scripts = [];
    for (const resource of resourceBuffer) {
        // Use decodedBodySize if available, otherwise fall back to encodedBodySize.
        // For cross-origin resources without Timing-Allow-Origin header, all size
        // fields are blocked (set to 0) regardless of cache status.
        const decodedSize = resource.decodedBodySize || 0;
        const encodedSize = resource.encodedBodySize || 0;
        const size = decodedSize || encodedSize;
        // If size is 0 but there was activity, size is CORS-blocked.
        const sizeUnavailable = size === 0 && resource.duration > 0;
        scripts.push({
            name: getFileName(resource.name),
            url: resource.name,
            size: size,
            transferSize: resource.transferSize || 0,
            duration: resource.duration,
            startTime: resource.startTime,
            type: resource.initiatorType === 'module' ? 'module' : 'script',
            sizeUnavailable: sizeUnavailable
        });
    }
    return scripts;
}
/**
 * Check if a script origin should be considered first-party
 * @param {string} scriptOrigin - The origin of the script to check
 * @param {string} currentOrigin - The origin of the current page
 * @returns {boolean} True if the script is first-party, false if third-party
 * @remarks
 * - Same-origin scripts are always considered first-party
 * - Additional domains can be configured in the firstPartyDomains array
 */
function isFirstParty(scriptOrigin, currentOrigin) {
    // Always consider same-origin as first-party
    if (scriptOrigin === currentOrigin) {
        return true;
    }
    // Add additional first-party origins/domains here
    /** @type {string[]} */
    const firstPartyDomains = [
        // this probably won't work due to missing header
        // 'import-map-assets-cdn.prod.merch.vpsvc.com',
        // Add more domains as needed
    ];
    try {
        const scriptUrl = new URL(scriptOrigin);
        return firstPartyDomains.some(
            (domain) => scriptUrl.hostname === domain || scriptUrl.hostname.endsWith('.' + domain)
        );
    } catch {
        return false;
    }
}
/**
 * Categorize scripts into first-party and third-party arrays
 * @param {ScriptInfo[]} scripts - Array of all loaded scripts
 * @param {string} currentOrigin - The origin of the current page
 * @returns {{ firstParty: ScriptInfo[], thirdParty: ScriptInfo[] }} Object containing categorized script arrays
 * @remarks
 * - Scripts are sorted by size (largest first) within each category
 * - If URL parsing fails, the script is treated as first-party
 */
function categorizeScripts(scripts, currentOrigin) {
    /** @type {ScriptInfo[]} */
    const firstParty = [];
    /** @type {ScriptInfo[]} */
    const thirdParty = [];
    for (const script of scripts) {
        try {
            const scriptUrl = new URL(script.url);
            const scriptOrigin = scriptUrl.origin;
            if (isFirstParty(scriptOrigin, currentOrigin)) {
                firstParty.push(script);
            } else {
                thirdParty.push(script);
            }
        } catch {
            // If URL parsing fails, treat as first-party
            firstParty.push(script);
        }
    }
    // Sort by size (largest first)
    firstParty.sort((a, b) => b.size - a.size);
    thirdParty.sort((a, b) => b.size - a.size);
    return { firstParty, thirdParty };
}
/**
 * Get import map data from the document
 * Looks for script elements with type="systemjs-importmap" or type="importmap"
 *
 * @returns {Object.<string, string>} Object mapping entrypoint names to URLs
 * @remarks
 * - Returns empty object if no import map is found or parsing fails
 * - Only returns the "imports" section of the import map
 */
function getImportMap() {
    const importMapScript = document.querySelector('script[type="systemjs-importmap"], script[type="importmap"]');
    if (!importMapScript || !importMapScript.textContent) {
        return {};
    }
    try {
        const importMap = JSON.parse(importMapScript.textContent);
        return importMap.imports || {};
    } catch (error) {
        console.warn('Failed to parse import map:', error);
        return {};
    }
}
/**
 * Group scripts by common base path (directory) or domain
 * Uses entrypoint names from import map when available
 *
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts
 * @param {ScriptInfo[]} thirdParty - Array of third-party scripts
 * @returns {Object.<string, ScriptGroup>} Object mapping base paths to script groups
 * @remarks
 * - Only includes entrypoints that were actually loaded
 * - Groups include all scripts matching the base path prefix
 * - Each script is assigned to exactly one group (no duplicates)
 * - Entrypoints sharing the same directory are combined into one group
 * - Third-party scripts are grouped by domain (origin)
 * - First-party scripts without entrypoints are grouped by directory
 * - Prevents duplicate entrypoint names within the same base path
 */
function groupScripts(firstParty, thirdParty) {
    const groups = {};
    const allScripts = [...firstParty, ...thirdParty];
    const imports = getImportMap();
    // Build a set of all loaded URLs for quick lookup
    const loadedUrls = new Set(allScripts.map((s) => s.url));
    // Build a mapping from base paths (full directory) to array of entrypoint names
    const basePathToNames = {};
    const entrypointBasePaths = [];
    for (const [entrypointName, entrypointUrl] of Object.entries(imports)) {
        if (typeof entrypointUrl !== 'string') continue;
        // Skip trailing slash entries
        if (entrypointName.endsWith('/')) continue;
        const resolvedUrl = resolveUrl(entrypointUrl);
        // Only include this entrypoint if it was actually loaded
        if (!loadedUrls.has(resolvedUrl)) {
            continue;
        }
        try {
            const urlObj = new URL(resolvedUrl);
            const entrypointBasePath = getBasePath(urlObj);
            // Map the full directory path to the entrypoint name
            if (!basePathToNames[entrypointBasePath]) {
                basePathToNames[entrypointBasePath] = [];
                entrypointBasePaths.push(entrypointBasePath);
            }
            if (!basePathToNames[entrypointBasePath].includes(entrypointName)) {
                basePathToNames[entrypointBasePath].push(entrypointName);
            }
        } catch {
            // Skip if URL parsing fails
        }
    }
    // Initialize groups for each entrypoint base path
    // Combine entrypoints that share the same directory into one group
    for (const basePath of entrypointBasePaths) {
        const entrypoints = basePathToNames[basePath];
        // Use the shortest package name for better grouping
        let name;
        if (entrypoints.length > 1) {
            // Extract package names and find the shortest one
            const packageNames = entrypoints.map((n) => getPackageName(n));
            const shortestPackage = packageNames.reduce((shortest, current) =>
                current.length < shortest.length ? current : shortest
            );
            name = `${shortestPackage}/*`;
        } else {
            name = entrypoints[0];
        }
        const basePrefix = basePath.endsWith('/') ? basePath : basePath + '/';
        groups[basePath] = {
            name,
            entrypoints,
            basePath: basePrefix,
            scripts: [],
            totalSize: 0,
            count: 0
        };
    }
    // Sort basePaths by length descending so the deepest match wins. Without
    // this, a parent-dir entrypoint (e.g. /lib/web for `react`) would claim
    // every script in its subtree before more-specific basePaths get a chance.
    const sortedBasePaths = [...entrypointBasePaths].sort((a, b) => b.length - a.length);

    // Assign each script to the most-specific entrypoint base path it lives under.
    for (const script of allScripts) {
        let matched = false;
        for (const basePath of sortedBasePaths) {
            const basePrefix = basePath.endsWith('/') ? basePath : basePath + '/';
            if (script.url.startsWith(basePrefix)) {
                groups[basePath].scripts.push(script);
                groups[basePath].totalSize += script.size;
                groups[basePath].count++;
                matched = true;
                break;
            }
        }
        // If no entrypoint match, create a standalone group
        // For third-party scripts, group by domain instead of directory
        if (!matched) {
            let basePath;
            let groupName;
            try {
                const scriptUrlObj = new URL(script.url);
                const scriptOrigin = scriptUrlObj.origin;
                // Check if this is a third-party script
                const currentOrigin = window.location.origin;
                const isThirdParty = !isFirstParty(scriptOrigin, currentOrigin);
                if (isThirdParty) {
                    // For third-party, group by domain (origin)
                    basePath = scriptOrigin;
                    groupName = scriptUrlObj.hostname; // Use hostname as the display name
                } else {
                    // For first-party, group by directory
                    basePath = getBasePath(scriptUrlObj);
                    groupName = basePath;
                }
            } catch {
                basePath = script.url;
                groupName = script.url;
            }
            if (!groups[basePath]) {
                groups[basePath] = {
                    name: groupName,
                    entrypoints: [],
                    basePath,
                    scripts: [],
                    totalSize: 0,
                    count: 0
                };
            }
            groups[basePath].scripts.push(script);
            groups[basePath].totalSize += script.size;
            groups[basePath].count++;
        }
    }
    return groups;
}
/**
 * Resolve a URL relative to the current page
 * @param {string} url - URL to resolve (can be relative or absolute)
 * @returns {string} Fully resolved absolute URL, or original URL if parsing fails
 * @example
 * resolveUrl('./script.js') // Returns 'https://example.com/path/script.js'
 * resolveUrl('https://cdn.com/lib.js') // Returns 'https://cdn.com/lib.js'
 */
function resolveUrl(url) {
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}
/**
 * Get base path for grouping (origin + directory path without filename)
 * @param {URL} urlObj - Parsed URL object
 * @returns {string} Base path consisting of origin and directory path
 * @example
 * getBasePath(new URL('https://example.com/path/to/script.js'))
 * // Returns 'https://example.com/path/to'
 */
function getBasePath(urlObj) {
    const pathParts = urlObj.pathname.split('/');
    pathParts.pop(); // Remove filename
    return urlObj.origin + pathParts.join('/');
}
/**
 * Extract npm package name from an entrypoint name
 * Supports both scoped (@org/package) and unscoped (package) package names
 *
 * @param {string} entrypointName - The entrypoint name from import map
 * @returns {string} The package name portion
 * @example
 * getPackageName('react') // Returns 'react'
 * getPackageName('react-dom') // Returns 'react-dom'
 * getPackageName('react/jsx-runtime') // Returns 'react'
 * getPackageName('react-dom/client') // Returns 'react-dom'
 * getPackageName('@vp/auth') // Returns '@vp/auth'
 * getPackageName('@vp/auth/utils') // Returns '@vp/auth'
 */
function getPackageName(entrypointName) {
    // Handle scoped packages (@org/package)
    if (entrypointName.startsWith('@')) {
        const parts = entrypointName.split('/');
        // Return @org/package (first two parts)
        return parts.slice(0, 2).join('/');
    }
    // Handle unscoped packages
    // Return everything before the first slash (or the whole string if no slash)
    const slashIndex = entrypointName.indexOf('/');
    return slashIndex === -1 ? entrypointName : entrypointName.substring(0, slashIndex);
}
/**
 * Find internal modules for an entrypoint by matching common path prefix
 * Uses the directory of the entrypoint as the base prefix for grouping related scripts
 *
 * @param {string} entrypointUrl - The URL of the entrypoint
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts to search through
 * @returns {ScriptInfo[]} Array of scripts that share the same directory path as the entrypoint
 * @remarks
 * - Only includes scripts from the same directory (matching base path prefix)
 * - Returns empty array if URL parsing fails
 */
function findInternalModules(entrypointUrl, firstParty) {
    const internalModules = [];
    try {
        const entrypointUrlObj = new URL(entrypointUrl);
        const entrypointBasePath = getBasePath(entrypointUrlObj);
        const basePrefix = entrypointBasePath.endsWith('/') ? entrypointBasePath : entrypointBasePath + '/';
        for (const script of firstParty) {
            if (script.url.startsWith(basePrefix)) {
                internalModules.push(script);
            }
        }
    } catch {
        // Skip if URL parsing fails
    }
    return internalModules;
}
/**
 * Parse import map and identify entrypoints with their internal modules
 * Groups entrypoints that share the same base path into a single entry
 *
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts
 * @param {ScriptInfo[]} thirdParty - Array of third-party scripts (used for loaded URL checking)
 * @returns {EntrypointInfo[]} Array of entrypoint information, sorted by total size (descending)
 * @remarks
 * - Only includes entrypoints that were actually loaded (present in Performance API)
 * - Entrypoints sharing the same directory are combined
 * - Uses shortest package name when combining multiple entrypoints
 * - Adds "/*" suffix to combined entrypoint names
 */
function analyzeImportMap(firstParty, thirdParty) {
    const imports = getImportMap();
    const allScripts = [...firstParty, ...thirdParty];
    // Build a set of all loaded URLs for quick lookup
    const loadedUrls = new Set(allScripts.map((s) => s.url));
    // Group entrypoints by their base path
    const basePathToEntrypoints = {};
    // First pass: collect entrypoints by base path
    for (const [entrypointName, entrypointUrl] of Object.entries(imports)) {
        if (typeof entrypointUrl !== 'string') continue;
        // Skip trailing slash entries
        if (entrypointName.endsWith('/')) continue;
        const resolvedUrl = resolveUrl(entrypointUrl);
        // Only include this entrypoint if it was actually loaded
        if (!loadedUrls.has(resolvedUrl)) {
            continue;
        }
        try {
            const urlObj = new URL(resolvedUrl);
            const basePath = getBasePath(urlObj);
            if (!basePathToEntrypoints[basePath]) {
                basePathToEntrypoints[basePath] = { names: [], urls: [] };
            }
            basePathToEntrypoints[basePath].names.push(entrypointName);
            basePathToEntrypoints[basePath].urls.push(resolvedUrl);
        } catch {
            // Skip if URL parsing fails
        }
    }
    // Second pass: collapse nested basePaths into their topmost ancestor.
    // When one entrypoint's basePath is a strict ancestor of another's
    // (e.g. ubik-react ships `react` flat at /dist/web/ while `react-dom/client`
    // lives at /dist/web/react-dom/), we treat the whole subtree as one
    // shipping unit. Without this, the parent's findInternalModules and the
    // child's both claim the same scripts, double-counting bytes.
    const allBasePaths = Object.keys(basePathToEntrypoints);
    const topmostBasePaths = allBasePaths.filter((bp) => {
        const ancestorPrefixes = allBasePaths
            .filter((other) => other !== bp)
            .map((other) => (other.endsWith('/') ? other : other + '/'));
        return !ancestorPrefixes.some((prefix) => bp.startsWith(prefix));
    });

    // Merge each non-topmost basePath's names/urls into its topmost ancestor.
    const collapsed = {};
    for (const top of topmostBasePaths) {
        collapsed[top] = { names: [], urls: [] };
    }
    for (const [bp, data] of Object.entries(basePathToEntrypoints)) {
        const top = topmostBasePaths.find((t) => {
            const tPrefix = t.endsWith('/') ? t : t + '/';
            return bp === t || bp.startsWith(tPrefix);
        });
        collapsed[top].names.push(...data.names);
        collapsed[top].urls.push(...data.urls);
    }

    const entrypoints = [];
    for (const [basePath, data] of Object.entries(collapsed)) {
        // Use the shortest package name as the group label when entries collapse together.
        let name;
        if (data.names.length > 1) {
            const packageNames = data.names.map((n) => getPackageName(n));
            const shortestPackage = packageNames.reduce((shortest, current) =>
                current.length < shortest.length ? current : shortest
            );
            name = `${shortestPackage}/*`;
        } else {
            name = data.names[0];
        }
        // findInternalModules grabs every script under the topmost basePath's
        // subtree — exactly what we want now that descendants are merged in.
        const internalModules = findInternalModules(data.urls[0], firstParty);
        entrypoints.push({
            name,
            url: basePath,
            internalModules,
            totalSize: internalModules.reduce((sum, m) => sum + m.size, 0)
        });
    }
    // Sort entrypoints by total size
    entrypoints.sort((a, b) => b.totalSize - a.totalSize);
    return entrypoints;
}
/**
 * Main data collection method to analyze all script resources on the page
 * Gathers performance data, categorizes scripts, identifies entrypoints, and creates script groups
 *
 * @returns {TrackingData} Complete tracking data including categorized scripts, entrypoints, and groups
 * @remarks
 * This is the primary data collection function that orchestrates all analysis steps:
 * 1. Collects all loaded scripts from Performance API
 * 2. Categorizes scripts as first-party or third-party
 * 3. Groups scripts by base path or domain
 * 4. Identifies entrypoints from import map and their internal modules
 */
function collect() {
    const currentOrigin = window.location.origin;
    const scripts = getLoadedScripts();
    const { firstParty, thirdParty } = categorizeScripts(scripts, currentOrigin);
    const groups = groupScripts(firstParty, thirdParty);
    const entrypoints = analyzeImportMap(firstParty, thirdParty);
    return {
        firstParty,
        thirdParty,
        entrypoints,
        groups
    };
}
/**
 * Generate a tracking summary with aggregated first-party entrypoints and third-party domains
 *
 * @returns {TrackingSummary} Summarized tracking data with counts and sizes
 * @remarks
 * This is the default export function that provides a simplified view of script data:
 * - First-party scripts are grouped by entrypoint name with total counts and sizes
 * - Unaccounted first-party scripts are grouped under "<unknown>"
 * - Third-party scripts are grouped by domain with counts and sizes (or undefined if CORS-blocked)
 * - Results are sorted by size (descending)
 *
 * @example
 * const summary = generateTrackingSummary();
 * console.log('First-party:', summary.firstParty);
 * console.log('Third-party:', summary.thirdParty);
 */
function generateTrackingSummary() {
    const data = collect();
    const currentOrigin = window.location.origin;
    // Map first-party entrypoints
    const firstPartyEntrypoints = data.entrypoints.map((entrypoint) => {
        return {
            name: entrypoint.name,
            count: entrypoint.internalModules.length,
            size: entrypoint.totalSize
        };
    });
    // Track which first-party scripts are accounted for in entrypoints
    const accountedScripts = new Set();
    data.entrypoints.forEach((entrypoint) => {
        entrypoint.internalModules.forEach((module) => {
            accountedScripts.add(module.url);
        });
    });
    // Find first-party scripts not in any entrypoint
    const unknownFirstParty = data.firstParty.filter((script) => !accountedScripts.has(script.url));
    // Add <unknown> group if there are unaccounted first-party scripts
    if (unknownFirstParty.length > 0) {
        const totalSize = unknownFirstParty.reduce((sum, script) => sum + script.size, 0);
        firstPartyEntrypoints.push({
            name: '<unknown>',
            count: unknownFirstParty.length,
            size: totalSize
        });
    }
    // Map third-party domains from groups
    const thirdPartyDomains = [];
    Object.values(data.groups).forEach((group) => {
        // Check if this group is third-party
        const isThirdParty = !group.basePath.startsWith(currentOrigin);
        if (isThirdParty) {
            // Count scripts with known sizes
            const modulesWithSize = group.scripts.filter((s) => s.size > 0).length;
            thirdPartyDomains.push({
                name: group.name,
                count: group.count,
                size: modulesWithSize > 0 ? group.totalSize : undefined
            });
        }
    });
    // Sort by size descending (treat undefined sizes as 0)
    thirdPartyDomains.sort((a, b) => (b.size || 0) - (a.size || 0));
    return {
        firstParty: firstPartyEntrypoints,
        thirdParty: thirdPartyDomains,
        firstPartyTotal: {
            count: firstPartyEntrypoints.reduce((sum, entry) => sum + entry.count, 0),
            size: firstPartyEntrypoints.reduce((sum, entry) => sum + entry.size, 0)
        },
        thirdPartyTotal: {
            count: thirdPartyDomains.reduce((sum, entry) => sum + entry.count, 0),
            size: thirdPartyDomains.reduce((sum, domain) => sum + (domain.size || 0), 0)
        }
    };
}

module.exports = {
    summary: generateTrackingSummary,
    collect,
    init
};

// Start observing at module load in real browsers. This is strictly earlier than
// any caller-driven init() — fewer ms during which a third-party could clear the
// performance buffer. Guarded so Node/SSR and non-browser test environments
// don't hit undefined globals at require() time.
if (typeof window !== 'undefined' && typeof PerformanceObserver !== 'undefined') {
    init();
}
